import { ko, type Messages } from './ko'
import { en } from './en'
import { ja } from './ja'
import { SOURCE_LANGUAGE, type LanguageId } from '../types'

const SOURCES: Record<LanguageId, Messages> = { ko, en, ja }

const resolved = new Map<LanguageId, Messages>()

/**
 * 그 언어의 사전을 준다. 값이 비어 나가면 한국어 정본으로 내린다.
 *
 * 키가 빠지는 일은 타입이 이미 막는다. 남는 것은 값이 빈 문자열로 들어가는 경우이고, 화면에
 * 빈자리를 두지 않기로 했으므로 그 자리에 한국어를 세운다. 어느 경우에도 키를 화면에 그리지
 * 않는다.
 *
 * 내림이 일어나면 개발 빌드에서 경고를 남긴다. 조용히 한국어로 그려지면 번역이 빠진 것을
 * 아무도 모른다.
 *
 * 한 번 만든 사전을 붙들어 둔다. 언어를 오갈 때마다 다시 만들면 화면이 그릴 때마다 참조가
 * 바뀌어 memo가 서지 않는다.
 */
export function messagesFor(languageId: LanguageId): Messages {
  const cached = resolved.get(languageId)
  if (cached) return cached

  const merged =
    languageId === SOURCE_LANGUAGE
      ? ko
      : (fillEmpty(SOURCES[languageId], ko, languageId) as Messages)
  resolved.set(languageId, merged)
  return merged
}

type Node = Record<string, unknown>

function fillEmpty(target: Node, source: Node, languageId: LanguageId, path = ''): Node {
  const filled: Node = {}
  for (const [key, sourceValue] of Object.entries(source)) {
    const targetValue = target[key]
    const here = path ? `${path}.${key}` : key

    if (typeof sourceValue === 'object' && sourceValue !== null) {
      filled[key] = fillEmpty(targetValue as Node, sourceValue as Node, languageId, here)
      continue
    }
    if (typeof targetValue === 'string' && targetValue.trim() === '') {
      warnFallback(languageId, here)
      filled[key] = sourceValue
      continue
    }
    filled[key] = targetValue
  }
  return filled
}

function warnFallback(languageId: LanguageId, key: string): void {
  if (!import.meta.env.DEV) return
  console.warn(`[i18n] ${languageId} 사전의 ${key}가 비어 한국어로 내렸다`)
}

export type { Messages }
